import { useSyncExternalStore } from 'react';

/**
 * Reactive store of experiment variant overrides — the QA path for previewing
 * an arm before (or regardless of) the flag ramp.
 *
 * Seeded once from the URL (`?<flagKey>=<variant>` for `exp.*` keys) so a
 * pasted preview link works, then mutated live by the experiments devtool
 * (`setOverride`) without a reload. Changes are mirrored back into the URL so
 * the current state is always shareable. Overridden slots render silently —
 * no exposure event, no super-property (see `ExperienceSlot`).
 */
type Listener = () => void;

const overrides = new Map<string, string>();
const listeners = new Set<Listener>();
let seeded = false;

/** Only `exp.*` params are override candidates — never generic query params. */
const OVERRIDE_KEY_PREFIX = 'exp.';

function seedFromUrl(): void {
  if (seeded) return;
  seeded = true;
  try {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key.startsWith(OVERRIDE_KEY_PREFIX) && value) overrides.set(key, value);
    });
  } catch {
    // URL unavailable/malformed — no seeds
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

/** Keep the address bar in sync so the current overrides stay shareable. */
function syncUrl(flagKey: string, variant: string | null): void {
  try {
    const url = new URL(window.location.href);
    if (variant) url.searchParams.set(flagKey, variant);
    else url.searchParams.delete(flagKey);
    window.history.replaceState(window.history.state, '', url);
  } catch {
    // history unavailable (tests, embeds) — the in-memory override still applies
  }
}

export function getOverride(flagKey: string): string | null {
  seedFromUrl();
  return overrides.get(flagKey) ?? null;
}

/** Set (or clear, with `null`) a variant override. Devtool + tests entry point. */
export function setOverride(flagKey: string, variant: string | null): void {
  seedFromUrl();
  if (variant) overrides.set(flagKey, variant);
  else overrides.delete(flagKey);
  syncUrl(flagKey, variant);
  emit();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive read of one experiment's override (null when none). */
export function useExperimentOverride(flagKey: string): string | null {
  return useSyncExternalStore(subscribe, () => getOverride(flagKey));
}

/** Test-only escape hatch. */
export function __resetOverridesForTests(): void {
  overrides.clear();
  seeded = false;
}
